import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { FolderPlus, Folder, Plus, Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CollectionManager({ 
  fontId, 
  collections = [], 
  onCollectionCreated,
  onFontAdded,
  compact = false 
}) {
  const { token } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [addingTo, setAddingTo] = useState(null);

  const handleCreateCollection = async () => {
    if (!newCollectionName.trim()) {
      toast.error('Please enter a collection name');
      return;
    }

    setIsCreating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/collections`,
        { name: newCollectionName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Collection "${newCollectionName}" created!`);
      setNewCollectionName('');
      setIsDialogOpen(false);
      
      if (onCollectionCreated) {
        onCollectionCreated(response.data);
      }
      
      // Auto-add font if fontId is provided
      if (fontId) {
        await addFontToCollection(response.data.id, response.data.name);
      }
    } catch (error) {
      console.error('Failed to create collection:', error);
      toast.error('Failed to create collection');
    } finally {
      setIsCreating(false);
    }
  };

  const addFontToCollection = async (collectionId, collectionName) => {
    if (!fontId) return;
    
    setAddingTo(collectionId);
    try {
      await axios.post(
        `${API_URL}/api/collections/${collectionId}/fonts`,
        { font_id: fontId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Added to "${collectionName}"`);
      
      if (onFontAdded) {
        onFontAdded(collectionId);
      }
    } catch (error) {
      console.error('Failed to add font to collection:', error);
      toast.error('Failed to add to collection');
    } finally {
      setAddingTo(null);
    }
  };

  if (compact) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="border-[#E5E5E5] hover:bg-[#0A0A0A] hover:text-white rounded-none"
            data-testid="add-to-collection-btn"
          >
            <FolderPlus className="w-4 h-4 mr-1" />
            Save
            <ChevronDown className="w-3 h-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 rounded-none border-[#E5E5E5]">
          {collections.length > 0 && (
            <>
              {collections.map((collection) => (
                <DropdownMenuItem
                  key={collection.id}
                  onClick={() => addFontToCollection(collection.id, collection.name)}
                  className="cursor-pointer"
                  data-testid={`collection-option-${collection.id}`}
                >
                  <Folder className="w-4 h-4 mr-2 text-[#FF3300]" />
                  {collection.name}
                  {addingTo === collection.id && (
                    <span className="ml-auto text-xs">Adding...</span>
                  )}
                </DropdownMenuItem>
              ))}
              <div className="border-t border-[#E5E5E5] my-1" />
            </>
          )}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <DropdownMenuItem 
                onSelect={(e) => e.preventDefault()}
                className="cursor-pointer"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Collection
              </DropdownMenuItem>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-none border-[#E5E5E5]">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'League Spartan, sans-serif' }}>
                  Create Collection
                </DialogTitle>
                <DialogDescription>
                  Organize your fonts into collections for easy access.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Input
                  placeholder="Collection name..."
                  value={newCollectionName}
                  onChange={(e) => setNewCollectionName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateCollection()}
                  className="rounded-none border-[#E5E5E5]"
                  data-testid="collection-name-input"
                />
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="rounded-none border-[#E5E5E5]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateCollection}
                  disabled={isCreating}
                  className="bg-[#FF3300] hover:bg-[#E62E00] rounded-none"
                  data-testid="create-collection-submit"
                >
                  {isCreating ? 'Creating...' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Full view for collections page
  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button
          className="bg-[#FF3300] hover:bg-[#E62E00] rounded-none"
          data-testid="create-collection-btn"
        >
          <FolderPlus className="w-4 h-4 mr-2" />
          New Collection
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md rounded-none border-[#E5E5E5]">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'League Spartan, sans-serif' }}>
            Create Collection
          </DialogTitle>
          <DialogDescription>
            Organize your fonts into collections for easy access.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            placeholder="Collection name..."
            value={newCollectionName}
            onChange={(e) => setNewCollectionName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateCollection()}
            className="rounded-none border-[#E5E5E5]"
            data-testid="collection-name-input"
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsDialogOpen(false)}
            className="rounded-none border-[#E5E5E5]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCollection}
            disabled={isCreating}
            className="bg-[#FF3300] hover:bg-[#E62E00] rounded-none"
            data-testid="create-collection-submit"
          >
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
